import { FC, useCallback, useRef, useState } from 'react'
import { useDebounce, useGetAddressSuggestions, useOnClickOutside } from '@/client/hooks'
import { IAddressSuggestion } from '@/client/types'
import * as S from './styles'
import { AddressAutocompleteProps } from './types'
import { Input } from '../Input'

export const AddressAutocomplete: FC<AddressAutocompleteProps> = ({
  value,
  onChange,
  onSelect,
  className,
  ...inputProps
}) => {
  const [term, setTerm] = useState<string>('')
  const [highlighted, setHighlighted] = useState<number>(-1)
  const boxRef = useRef<HTMLDivElement>(null)
  const debouncedTerm = useDebounce(term, 400)
  const { data } = useGetAddressSuggestions(debouncedTerm)
  const suggestions = data ?? []

  const isOpen = Boolean(term) && Boolean(suggestions.length)

  const handleCloseList = useCallback(() => {
    setTerm('')
    setHighlighted(-1)
  }, [])

  useOnClickOutside(boxRef, handleCloseList)

  const handleChange = useCallback(
    (event) => {
      const { value: nextValue } = event.target
      onChange?.(event)
      setTerm(nextValue)
      setHighlighted(-1)
    },
    [onChange]
  )

  const handleSelect = useCallback(
    (suggestion: IAddressSuggestion) => {
      handleCloseList()
      onSelect(suggestion)
    },
    [handleCloseList, onSelect]
  )

  const handleKeyDown = useCallback(
    (event) => {
      if (!isOpen) return

      if (event.key === 'ArrowDown') {
        event.preventDefault()
        setHighlighted((old) => (old + 1) % suggestions.length)
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault()
        setHighlighted((old) => (old <= 0 ? suggestions.length - 1 : old - 1))
      }

      if (event.key === 'Enter' && highlighted >= 0) {
        event.preventDefault()
        handleSelect(suggestions[highlighted])
      }

      if (event.key === 'Escape') handleCloseList()
    },
    [handleCloseList, handleSelect, highlighted, isOpen, suggestions]
  )

  return (
    <S.Container ref={boxRef} className={className}>
      <Input
        {...inputProps}
        type='text'
        value={value}
        autoComplete='off'
        role='combobox'
        aria-expanded={isOpen}
        aria-autocomplete='list'
        onChange={handleChange}
        onKeyDown={handleKeyDown}
      />

      {isOpen && (
        <S.SuggestionList role='listbox'>
          {suggestions.map((suggestion, index) => (
            <S.SuggestionItem
              key={suggestion.placeId}
              role='option'
              aria-selected={index === highlighted}
              $isHighlighted={index === highlighted}
              onMouseEnter={() => setHighlighted(index)}
              onMouseDown={() => handleSelect(suggestion)}
            >
              {suggestion.label}
            </S.SuggestionItem>
          ))}
        </S.SuggestionList>
      )}
    </S.Container>
  )
}
